import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import {
  Edit as EditIcon,
  Rule as RuleIcon,
  Notifications as NotificationsIcon,
  OpenInNew as OpenInNewIcon,
  VolumeOff as SilenceIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { MoreActionsMenuProps, MenuAction, CARD_BORDER_RADIUS } from '../types';

/**
 * MoreActionsMenu - Dropdown menu with common actions for exporters and websites.
 *
 * Provides quick access to:
 * - Edit the target
 * - Manage alert rules
 * - Add notification channel
 * - Open in Grafana
 * - Quick silence (1hr)
 * - Disable monitoring
 * - Delete
 */
export function MoreActionsMenu({
  anchorEl,
  open,
  onClose,
  targetType,
  isDisabled,
  onAction,
}: MoreActionsMenuProps) {
  const handleAction = (action: MenuAction) => {
    onAction(action);
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          minWidth: 200,
          borderRadius: CARD_BORDER_RADIUS,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        },
      }}
    >
      <MenuItem onClick={() => handleAction('edit')}>
        <ListItemIcon>
          <EditIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>
          Edit {targetType === 'exporter' ? 'Exporter' : 'Website'}
        </ListItemText>
      </MenuItem>

      {targetType === 'exporter' && (
        <MenuItem onClick={() => handleAction('manageAlerts')}>
          <ListItemIcon>
            <RuleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Manage Alert Rules</ListItemText>
        </MenuItem>
      )}

      <MenuItem onClick={() => handleAction('addNotification')}>
        <ListItemIcon>
          <NotificationsIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Add Notification</ListItemText>
      </MenuItem>

      <Divider />

      {targetType === 'exporter' && (
        <MenuItem onClick={() => handleAction('openGrafana')}>
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open in Grafana</ListItemText>
        </MenuItem>
      )}

      <Divider />

      {targetType === 'exporter' && (
        <MenuItem onClick={() => handleAction('quickSilence')}>
          <ListItemIcon>
            <SilenceIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Quick Silence (1hr)</ListItemText>
        </MenuItem>
      )}

      <MenuItem onClick={() => handleAction(isDisabled ? 'enable' : 'disable')}>
        <ListItemIcon>
          {isDisabled ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
        </ListItemIcon>
        <ListItemText>{isDisabled ? 'Enable Monitoring' : 'Disable Monitoring'}</ListItemText>
      </MenuItem>

      <MenuItem onClick={() => handleAction('delete')} sx={{ color: 'error.main' }}>
        <ListItemIcon>
          <DeleteIcon fontSize="small" color="error" />
        </ListItemIcon>
        <ListItemText>Delete</ListItemText>
      </MenuItem>
    </Menu>
  );
}

export default MoreActionsMenu;
